import React, { useState, useEffect } from 'react';
import { userService } from '../services/api';

const UserManager = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [hasAdminAccess, setHasAdminAccess] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const USERS_PER_PAGE = 10;

  useEffect(() => {
    loadUsers();
  }, [currentPage]);

  const loadUsers = async (page = currentPage) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('Attempting to load users for page:', page);
      
      const response = await userService.getUsers(page, USERS_PER_PAGE, 'UTC');
      console.log('Users loaded successfully:', response);
      
      // Handle the response format - userService returns axios response object
      let usersData = [];
      let pagination = {};
      
      if (response.data && response.data.data) {
        usersData = response.data.data;
        pagination = response.data.pagination || {};
      } else if (response.data) {
        usersData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (response) {
        usersData = Array.isArray(response) ? response : [response];
      }
      
      console.log('Processed users data:', usersData);
      console.log('Pagination info:', pagination);
      
      if (Array.isArray(usersData)) {
        setUsers(usersData);
        setTotalPages(pagination.total_pages || Math.ceil((pagination.total || usersData.length) / USERS_PER_PAGE));
        setTotalUsers(pagination.total || usersData.length);
        setCurrentPage(page);
        setHasAdminAccess(true);
      } else {
        console.warn('Invalid users response format:', usersData);
        setUsers([]);
      }
      
    } catch (err) {
      console.error('Failed to load users:', err);
      
      // Check if it's a permission error (403) or auth error (401)
      if (err.response?.status === 403) {
        setHasAdminAccess(false);
        setError('You do not have permission to manage users. This feature is only available to administrators.');
      } else if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(`Failed to load users: ${err.message || 'Unknown error'}`);
      }
      
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUser = async (userId, currentStatus) => {
    if (!hasAdminAccess) {
      setError('You do not have permission to modify users.');
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      
      console.log(`Toggling user ${userId} from ${currentStatus ? 'active' : 'inactive'} to ${!currentStatus ? 'active' : 'inactive'}`);
      
      const updatedUser = await userService.updateUser(userId, { 
        is_active: !currentStatus 
      });
      
      console.log('User updated:', updatedUser);
      
      // Update the user in the local state
      setUsers(prevUsers => 
        prevUsers.map(user => 
          user.id === userId 
            ? { ...user, is_active: !currentStatus }
            : user
        )
      );
      
      setSuccessMessage(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (err) {
      console.error('Failed to toggle user:', err);
      
      if (err.response?.status === 403) {
        setError('You do not have permission to modify users.');
      } else if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(`Failed to update user: ${err.message || 'Unknown error'}`);
      }
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!hasAdminAccess) {
      setError('You do not have permission to delete users.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      setSuccessMessage('');
      
      console.log(`Deleting user ${userId}`);
      
      await userService.deleteUser(userId);
      
      console.log('User deleted successfully');
      
      // Remove the user from the local state
      setUsers(prevUsers => prevUsers.filter(user => user.id !== userId));
      
      setSuccessMessage('User deleted successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (err) {
      console.error('Failed to delete user:', err);
      
      if (err.response?.status === 403) {
        setError('You do not have permission to delete users.');
      } else if (err.response?.status === 401) {
        setError('Your session has expired. Please log in again.');
      } else {
        setError(`Failed to delete user: ${err.message || 'Unknown error'}`);
      }
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage);
    }
  };

  // Helper function to get role name from role ID
  const getRoleName = (roleId) => {
    switch (roleId) {
      case 1: return 'Super Admin';
      case 2: return 'Admin';  
      case 3: return 'Staff';
      default: return 'Unknown';
    }
  };

  // Helper function to get role badge color
  const getRoleColor = (roleId) => {
    switch (roleId) {
      case 1: return 'super-admin';
      case 2: return 'admin';
      case 3: return 'staff';
      default: return 'unknown';
    }
  };

  if (loading) {
    return (
      <div className="user-manager">
        <div className="user-header">
          <h2>User Management</h2>
        </div>
        <div className="loading-message">Loading users...</div>
      </div>
    );
  }

  if (!hasAdminAccess) {
    return (
      <div className="user-manager">
        <div className="user-header">
          <h2>User Management</h2>
        </div>
        <div className="users-content">
          <div className="empty-state">
            <i className="fas fa-lock" style={{ fontSize: '48px', color: '#6c757d', marginBottom: '16px' }}></i>
            <h3>Access Restricted</h3>
            <p>You do not have permission to access user management features.</p>
            <p>This section is only available to administrators.</p>
            <p>Please contact your system administrator if you need access to these features.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-manager">
      <div className="user-header">
        <h2>User Management</h2>
        <div className="header-controls">
          {totalUsers > 0 && (
            <span className="user-count">
              {totalUsers} total users
            </span>
          )}
          <button 
            className="refresh-btn" 
            onClick={() => loadUsers(currentPage)}
            disabled={loading}
          >
            <i className="fas fa-sync-alt"></i>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          {error}
        </div>
      )}

      {successMessage && (
        <div className="success-message">
          <i className="fas fa-check-circle"></i>
          {successMessage}
        </div>
      )}

      <div className="users-content">
        {users.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-users" style={{ fontSize: '48px', color: '#6c757d', marginBottom: '16px' }}></i>
            <h3>No Users Found</h3>
            <p>No users are currently available to manage.</p>
          </div>
        ) : (
          <>
            <div className="users-table-container">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div className="user-name">
                          <i className="fas fa-user"></i>
                          {user.name}
                        </div>
                      </td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${getRoleColor(user.role_id)}`}>
                          {getRoleName(user.role_id)}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="user-actions">
                          <button
                            className={`action-btn toggle ${user.is_active ? 'deactivate' : 'activate'}`}
                            onClick={() => handleToggleUser(user.id, user.is_active)}
                            title={user.is_active ? 'Deactivate User' : 'Activate User'}
                          >
                            <i className={`fas ${user.is_active ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                            {user.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            title="Delete User"
                          >
                            <i className="fas fa-trash"></i>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <i className="fas fa-chevron-left"></i>
                  Previous
                </button>
                
                <div className="pagination-info">
                  <span>Page {currentPage} of {totalPages}</span>
                  <span className="pagination-details">
                    Showing {Math.min((currentPage - 1) * USERS_PER_PAGE + 1, totalUsers)} - {Math.min(currentPage * USERS_PER_PAGE, totalUsers)} of {totalUsers} users
                  </span>
                </div>
                
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserManager; 